// The four keypads of the IR remote — the KEYS only.
//
// The body they sit in, the tab bar and the close button belong to
// RemoteHandset; this file is the pads and nothing else, so the same keys can be
// dropped into a floating handset on the desktop and a full-height one on a
// phone without either shell owning a layout the other has to work around.
//
// ── Why it looks like a handset ────────────────────────────────────────────
// Because it IS one. This is a keypad the user already owns a physical copy of,
// sitting on the arm of their sofa. Laid out as a web form — full-width buttons
// in rows across a page — every key is the same size and shape, and finding
// "volume down" means reading rather than reaching. A remote is navigated by
// muscle memory and relative position: round digits in a 3×4 block, a D-pad in a
// cross, volume and channel in facing columns. Keeping those shapes is what
// makes it usable without looking at it.
//
// ⚠️ Every literal comes from core/devices/irRemote. No component in this file
// contains a command string, and none should: a token is matched exactly by the
// firmware and a wrong one fails silently at both ends.

import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, Slider, Tooltip,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  DEN_KEYS, TV_KEYS, LIGHT_KEYS, LIGHT_CHANNELS, AC_KEYS,
  AC_TEMP_MIN, AC_TEMP_MAX, acTemperature, denChannel,
} from '../../../core/devices/irRemote';
import { getFavouriteChannels } from '../../../core/api/remoteCatalog';
import { useApp } from '../../../core/state/AppContext';

// ── Key shapes, and the physics they share ─────────────────────────────────
//
// A key on a real remote is an EXTRUDED piece of rubber standing proud of the
// case, and three cues sell that: a lit top edge where the light hits it, a
// solid coloured skirt underneath (its thickness, not a blur), and a soft
// contact shadow on the case below. Press it and all three collapse — the cap
// travels down onto the board and the skirt disappears.
//
// That last part is what makes it feel physical rather than painted: the key
// must MOVE by exactly the height of its own skirt. `translateY(3px)` against a
// `0 3px 0` skirt lands the cap where the skirt was, so nothing slides.
//
// `transition` covers transform and shadow only — never `background`, or the
// press lags behind the finger.

/**
 * @param face   the cap colour (top of the gradient is lightened by the inset)
 * @param skirt  the side of the key — its thickness. Must be darker than face.
 * @param depth  how far it stands proud, in px. The travel distance too.
 */
const extruded = (face, skirt, depth = 3) => ({
  minWidth: 0,
  textTransform: 'none',
  color: '#E8ECF4',
  border: 0,
  backgroundImage: `linear-gradient(180deg, ${face} 0%, ${skirt} 100%)`,
  boxShadow: [
    'inset 0 1px 0 rgba(255,255,255,0.22)', // the lit top edge
    'inset 0 -1px 0 rgba(0,0,0,0.35)', // the shaded underside of the cap
    `0 ${depth}px 0 ${skirt}`, // the skirt: solid, not blurred
    `0 ${depth + 2}px ${depth + 4}px rgba(0,0,0,0.45)`, // contact shadow
  ].join(', '),
  transition: 'transform 70ms ease-out, box-shadow 70ms ease-out, filter 120ms',
  '&:hover': { filter: 'brightness(1.18)' },
  '&:active': {
    // Travels exactly its own depth, so the cap lands where the skirt was.
    transform: `translateY(${depth}px)`,
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.10)',
      'inset 0 2px 6px rgba(0,0,0,0.45)', // pressed into the case
      '0 0 0 rgba(0,0,0,0)',
      '0 1px 2px rgba(0,0,0,0.4)',
    ].join(', '),
  },
  // A dead key is FLUSH with the case — no skirt, no shadow, nothing to press.
  '&.Mui-disabled': {
    color: 'rgba(232,236,244,0.25)',
    backgroundImage: 'none',
    bgcolor: 'rgba(255,255,255,0.03)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.45)',
  },
});

const ROUND = {
  ...extruded('#3A414F', '#232833'),
  width: 42,
  height: 42,
  borderRadius: '50%',
  p: 0,
  fontSize: 14,
  fontWeight: 600,
};

const PILL = {
  ...extruded('#333A47', '#20252F'),
  borderRadius: 7,
  py: 0.55,
  px: 1,
  fontSize: 11,
  lineHeight: 1.1,
  fontWeight: 600,
};

// Domed, and the only saturated key on the case — the one you find blind.
const POWER = {
  ...extruded('#E04B4B', '#8E1F1F', 4),
  width: 46,
  height: 46,
  borderRadius: '50%',
  p: 0,
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
};

/*
 * ⚠️ AN ACCENT KEY NEEDS ITS OWN FACE **AND** SKIRT, not a `bgcolor` override.
 * The extrusion is a gradient plus a skirt shadow in a matching colour; setting
 * `bgcolor` on top paints a flat fill over the gradient and leaves the skirt the
 * wrong colour, so the key goes flat while its neighbours stay raised — which
 * reads as a rendering fault rather than a highlight.
 */
const POWER_ON = { ...POWER, ...extruded('#43A047', '#1B5E20', 4) };
const OK_KEY = { ...ROUND, ...extruded('#3D8BD8', '#17518F') };
const ALL_ON = { ...PILL, ...extruded('#43A047', '#1B5E20') };

export function Key({
  token, label, icon, onSend, shape = PILL, title,
}) {
  // A key with no token is drawn DISABLED rather than dropped: the pad copies a
  // physical remote, and a missing button is more confusing than a dead one.
  // Six AC keys are in this state — the Flutter app never wired them either.
  const dead = !token;
  return (
    <Tooltip title={title || (dead ? 'No command for this key in the firmware' : token)} placement="top">
      <span>
        <Button disableRipple={false} disabled={dead} onClick={() => onSend(token)} sx={shape}>
          {icon || label}
        </Button>
      </span>
    </Tooltip>
  );
}

const RowSx = { display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' };
const GridSx = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 42px)', gap: 1, justifyContent: 'center',
};

function DigitPad({ digits, onSend }) {
  return (
    <Box sx={GridSx}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
        <Key key={d} token={digits[d]} label={String(d)} shape={ROUND} onSend={onSend} />
      ))}
      <Box />
      <Key token={digits[0]} label="0" shape={ROUND} onSend={onSend} />
      <Box />
    </Box>
  );
}

/** Volume | (middle) | Channel — the facing columns every handset has. */
function RockerRow({
  volUp, volDown, chUp, chDown, middle, onSend,
}) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
        <Key token={volUp} icon={<VolumeUpIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Typography sx={{ fontSize: 9, color: 'rgba(232,236,244,0.5)' }}>VOL</Typography>
        <Key token={volDown} icon={<VolumeDownIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>{middle}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
        <Key token={chUp} icon={<KeyboardArrowUpIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Typography sx={{ fontSize: 9, color: 'rgba(232,236,244,0.5)' }}>CH</Typography>
        <Key token={chDown} icon={<KeyboardArrowDownIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
      </Box>
    </Box>
  );
}

export function DenPad({ onSend }) {
  const k = DEN_KEYS;
  const { userId } = useApp();
  const [favourites, setFavourites] = useState([]);

  // Best-effort: the pad is fully usable without them, so a failure leaves the
  // strip empty rather than breaking the remote.
  useEffect(() => {
    let alive = true;
    if (!userId) return undefined;
    getFavouriteChannels(userId)
      .then((list) => { if (alive) setFavourites(list); })
      .catch(() => {});
    return () => { alive = false; };
  }, [userId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={RowSx}>
        {/* ⚠️ There is no DNOFF — one toggling power key. */}
        <Key token={k.power} icon={<PowerSettingsNewIcon />} shape={POWER} onSend={onSend} title="Power (toggles)" />
        <Key token={k.mute} icon={<VolumeOffIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Key token={k.info} label="Info" shape={ROUND} onSend={onSend} />
      </Box>

      <DigitPad digits={k.digits} onSend={onSend} />

      <RockerRow
        volUp={k.volUp}
        volDown={k.volDown}
        chUp={k.chUp}
        chDown={k.chDown}
        onSend={onSend}
        middle={(
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Key token={k.guide} label="Guide" onSend={onSend} />
            <Key token={k.home} label="Home" onSend={onSend} />
            <Key token={k.back} label="Back" onSend={onSend} />
            <Key token={k.exit} label="Exit" onSend={onSend} />
          </Box>
        )}
      />

      {favourites.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 9, color: 'rgba(232,236,244,0.5)', mb: 0.5 }}>FAVOURITES</Typography>
          {/* One tap = DNCHOICE<n>, the same fire-and-forget token as any key. */}
          <Box data-hscroll sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}>
            {favourites.map((f) => (
              <Button
                key={f.channelNumber}
                onClick={() => onSend(denChannel(f.channelNumber))}
                sx={{ ...PILL, flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {`${f.channelNumber} · ${f.channelName}`}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function TvPad({ onSend }) {
  const k = TV_KEYS;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={RowSx}>
        <Key token={k.power} icon={<PowerSettingsNewIcon />} shape={POWER} onSend={onSend} title="Power (toggles)" />
        <Key token={k.mute} icon={<VolumeOffIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Key token={k.source} label="Src" shape={ROUND} onSend={onSend} />
      </Box>

      <DigitPad digits={k.digits} onSend={onSend} />

      {/* D-pad, in a cross — reachable by feel. */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 42px)', gap: 0.75, justifyContent: 'center',
      }}
      >
        <Key token={k.menu} label="Menu" onSend={onSend} />
        <Key token={k.up} icon={<KeyboardArrowUpIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Key token={k.exit} label="Exit" onSend={onSend} />
        <Key token={k.left} icon={<KeyboardArrowLeftIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Key token={k.ok} label="OK" shape={OK_KEY} onSend={onSend} />
        <Key token={k.right} icon={<KeyboardArrowRightIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Key token={k.back} label="Back" onSend={onSend} />
        <Key token={k.down} icon={<KeyboardArrowDownIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
        <Key token={k.home} label="Home" onSend={onSend} />
      </Box>

      <RockerRow
        volUp={k.volUp}
        volDown={k.volDown}
        chUp={k.chUp}
        chDown={k.chDown}
        onSend={onSend}
        middle={(
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Key token={k.guide} label="Guide" onSend={onSend} />
            <Key token={k.info} label="Info" onSend={onSend} />
            <Key token={k.dashDash} label="-/--" onSend={onSend} />
          </Box>
        )}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
        <Key token={k.input} label="Input" onSend={onSend} />
        <Key token={k.hdmi} label="HDMI" onSend={onSend} />
        <Key token={k.zoom} label="Zoom" onSend={onSend} />
        <Key token={k.sleep} label="Sleep" onSend={onSend} />
        <Key token={k.tools} label="Tools" onSend={onSend} />
        <Key token={k.search} label="Search" onSend={onSend} />
        <Key token={k.prevCh} label="Pre-ch" onSend={onSend} />
        <Key token={k.fav} label="Fav" onSend={onSend} />
        <Key token={k.disp} label="Disp" onSend={onSend} />
        <Key token={k.auto} label="Auto" onSend={onSend} />
        <Key token={k.cal} label="Cal" onSend={onSend} />
        <Key token={k.echo} label="Echo" onSend={onSend} />
      </Box>
    </Box>
  );
}

export function LightPad({ onSend }) {
  const k = LIGHT_KEYS;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* ⚠️ RTAllon / RTAlloff — mixed case, and NOT the ALLON/ALLOFF verb the
            touch panels and BLE use. Different device, different vocabulary. */}
        <Button
          fullWidth
          onClick={() => onSend(k.allOn)}
          sx={{ ...ALL_ON, py: 1, fontSize: 12 }}
        >
          ALL ON
        </Button>
        <Button
          fullWidth
          onClick={() => onSend(k.allOff)}
          sx={{ ...PILL, py: 1, fontSize: 12 }}
        >
          ALL OFF
        </Button>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 9, color: 'rgba(232,236,244,0.5)', mb: 0.5 }}>CHANNELS</Typography>
        <Box sx={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 42px)', gap: 1, justifyContent: 'center',
        }}
        >
          {LIGHT_CHANNELS.map((t, i) => (
            <Key key={t} token={t} label={String(i + 1)} shape={ROUND} onSend={onSend} />
          ))}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 9, color: 'rgba(232,236,244,0.5)', mb: 0.5 }}>FANS</Typography>
        {/* Each fan is a column: + / OFF / − , the way the speed keys sit on the
            handset. ⚠️ The OFF keys are disabled because the firmware has no
            word for them — see LIGHT_KEYS.fan1Off. They are drawn rather than
            omitted so the shape is right the moment a token exists. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
            <Key token={k.fan1Up} label="Fan 1 +" onSend={onSend} />
            <Key
              token={k.fan1Off}
              label="Fan 1 off"
              onSend={onSend}
              title="No fan-off command exists in the firmware yet"
            />
            <Key token={k.fan1Down} label="Fan 1 −" onSend={onSend} />
          </Box>
          <Key token={k.mute} icon={<VolumeOffIcon fontSize="small" />} shape={ROUND} onSend={onSend} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
            <Key token={k.fan2Up} label="Fan 2 +" onSend={onSend} />
            <Key
              token={k.fan2Off}
              label="Fan 2 off"
              onSend={onSend}
              title="No fan-off command exists in the firmware yet"
            />
            <Key token={k.fan2Down} label="Fan 2 −" onSend={onSend} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function AcPad({ onSend }) {
  /*
   * ⚠️ THE DIAL STARTS UNSET, and that is the design, not an omission.
   *
   * The blaster never reports its setpoint — see the five live Status shapes in
   * core/devices/irRemote. Flutter seeded this from index 10 of a CSV that no
   * deployed device sends, so it opened showing a temperature the appliance was
   * not on. An unset dial says "I do not know", which is true; a wrong number
   * says something false and then lets the user send it.
   */
  const [temp, setTemp] = useState(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={RowSx}>
        <Key token={AC_KEYS.on} label="ON" shape={POWER_ON} onSend={onSend} />
        <Key token={AC_KEYS.off} label="OFF" shape={POWER} onSend={onSend} />
      </Box>

      {/* The setpoint, shown big — it is the one number on this pad. */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontSize: 40, fontWeight: 300, color: '#E8ECF4', lineHeight: 1 }}>
          {temp == null ? '—' : temp}
          <Typography component="span" sx={{ fontSize: 16, ml: 0.5, color: 'rgba(232,236,244,0.6)' }}>°C</Typography>
        </Typography>
        <Slider
          size="small"
          min={AC_TEMP_MIN}
          max={AC_TEMP_MAX}
          step={1}
          value={temp == null ? AC_TEMP_MIN : temp}
          marks
          onChange={(_, v) => setTemp(v)}
          // ⚠️ ON RELEASE ONLY. Publishing per pixel would blast a dozen
          // setpoints at an appliance that cannot tell us which it took.
          onChangeCommitted={(_, v) => onSend(acTemperature(v))}
          sx={{ mt: 1, color: '#4FC3F7' }}
        />
        <Typography sx={{ fontSize: 9, color: 'rgba(232,236,244,0.45)' }}>
          {temp == null
            ? 'The blaster does not report its setting'
            : 'What you set — not read back from the AC'}
        </Typography>
      </Box>

      <Button onClick={() => onSend(AC_KEYS.hiPower)} sx={{ ...PILL, py: 0.9 }}>
        Power Chill / Hi Power
      </Button>

      {/* The six keys the firmware has no word for. Shown, disabled, because
          they are on the physical remote — see irRemote's "not ported" note. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
        {['Mode', 'Fan', 'Sleep', 'Swing', 'Turbo', 'LED'].map((label) => (
          <Key key={label} token={null} label={label} onSend={onSend} />
        ))}
      </Box>
    </Box>
  );
}

/** The pads, in the Flutter tab order (remote_main_page.dart:37-42). */
export const REMOTE_PADS = [
  { id: 'DEN', label: 'DEN', Pad: DenPad },
  { id: 'AC', label: 'AC', Pad: AcPad },
  { id: 'TV', label: 'TV', Pad: TvPad },
  { id: 'LIGHT', label: 'LIGHTS', Pad: LightPad },
];

export default REMOTE_PADS;
